import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./achievements.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import { FeatureFlags } from '~/modules/feature_flags';
import { ACHIEVEMENT_IMAGE_URLS, getListingPublisher, POTY_IMAGE_BY_CYCLE_YEAR, selectProviderAchievements } from '~/modules/helpers/listingHelper';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { routerStore, selectScrollToQueryParam } from '~/modules/stores/routerStore';
import { scrollToElement } from '~/modules/utilities/browser';
/**
 * Provider Achievements section for the consultant listing Overview tab. Renders
 * one media card per achievement the listing has earned (Summit tier, Forward
 * Deployed Engineering partner), built on the `wds-media-card` primitive.
 */
class Achievements extends LightningElement {
  constructor(...args) {
    super(...args);
    this.listing = void 0;
    this.i18n = void 0;
    // Drives the scroll-to-achievements jump from the header award deck. Owned here
    // rather than by the embedding Overview panels so focus can land on this
    // section's own heading (announced as "…Achievements, heading") instead of a
    // generic wrapper. Consumed in renderedCallback once the heading has rendered.
    this.scrollToParam = void 0;
  }
  renderedCallback() {
    // moveFocus: move the screen-reader/keyboard reading position onto the
    // heading (see data-scroll-to in the template), not just the viewport.
    scrollToElement(this, this.scrollToParam, true);
  }
  get providerName() {
    return getListingPublisher(this.listing)?.name ?? '';
  }
  get heading() {
    return i18nStore.getState().i18n.heading?.providerAchievements({
      PROVIDER_NAME: this.providerName
    });
  }
  get achievements() {
    // Partner of the Year is gated only on its own flag here; whether the
    // section renders at all (and any FDE-badge gating) is owned by the
    // embedding panel's mount decision — v1 requires FDE, v2 does not.
    // Consultant achievements (Summit tier / FDE partner) are added by the
    // selector independent of this arg.
    return selectProviderAchievements(this.listing, FeatureFlags.FF_TRYBUY_POTY_BADGE);
  }
  get hasAchievements() {
    return this.achievementCards.length > 0;
  }
  get achievementCards() {
    const achievements = this.i18n?.listingDetails?.achievements;
    if (!achievements) {
      return [];
    }
    const ctaLabel = this.i18n.shared.learnMore;
    return this.achievements.flatMap(key => {
      if (key === 'partnerOfYear') {
        return this.buildPartnerOfYearCards(achievements, ctaLabel);
      }
      const {
        category,
        title,
        url
      } = achievements[key];
      return [{
        key,
        category,
        title,
        // Decorative image; empty alt text avoids repeating the adjacent title.
        image: {
          url: ACHIEVEMENT_IMAGE_URLS[key],
          altText: ''
        },
        callToAction: url ? {
          name: `achievement-${key}`,
          label: ctaLabel,
          url
        } : null,
        // Card-specific accessible name so links sharing the "Learn More" label stay distinguishable.
        ctaAriaLabel: url ? i18nStore.getState().i18n.shared.learnMoreAbout({
          FEATURE: title
        }) : undefined
      }];
    });
  }

  /**
   * One card per publisher award (current and past). Kept separate from the
   * single-card Summit/FDE mapping in {@link achievementCards} because a single
   * `partnerOfYear` key fans out to N award cards.
   */
  buildPartnerOfYearCards(achievements, ctaLabel) {
    const potyUrl = achievements.partnerOfYear?.url;
    return (this.listing?.publisherAwards?.awards ?? []).map((award, index) => {
      // Drop parentheses from the award name (e.g. "Customer Value (CSAT)"
      // -> "Customer Value CSAT"); collapse any whitespace the removal leaves.
      const cardTitle = (award.awardName ?? '').replace(/[()]/g, '').replace(/\s{2,}/g, ' ').trim();
      // The accessible link name folds in the cycle year so awards that
      // share a name across years stay distinguishable to assistive tech
      // (WCAG 2.4.4) — the `for:each` key alone never reaches the a11y
      // tree. The visible title stays year-free.
      const ctaFeature = award.cycleYear ? `${cardTitle} (${award.cycleYear})` : cardTitle;
      // Each cycle has its own badge art; a year whose asset is not published
      // yet renders no image (blank card) rather than reusing another year's.
      const imageUrl = award.cycleYear != null ? POTY_IMAGE_BY_CYCLE_YEAR[award.cycleYear] : undefined;
      // Award sub-designation (e.g. 'CSAT'), read defensively. awardSubName is
      // returned at runtime (publishing-service v1.706.0+) but not yet in the
      // published @oneappexchange/oaf-schema type, so it arrives untyped. Accept
      // it only when it is actually a string — anything else (or absent) leaves
      // the subtitle undefined, so the card's if:true drops the node and keeps
      // its tight title spacing (no empty-subtitle padding).
      // TODO(W-23953317): once oaf-schema publishes awardSubName, drop this guard
      // and read award.awardSubName directly.
      const rawSubName = award.awardSubName;
      const awardSubtitle = typeof rawSubName === 'string' ? rawSubName : undefined;
      return {
        // cycleYear stays in the key (not the visible title) to keep sibling
        // cards distinct when awards share a name across years.
        key: `partnerOfYear-${award.external_id ?? `${award.awardName}-${award.cycleYear}-${index}`}`,
        category: award.category ?? achievements.partnerOfYear?.category,
        title: cardTitle,
        // Sub-designation shown under the title (guarded above).
        subtitle: awardSubtitle,
        // Decorative image; empty alt text avoids repeating the adjacent title.
        image: imageUrl ? {
          url: imageUrl,
          altText: ''
        } : null,
        callToAction: potyUrl ? {
          name: `achievement-poty-${award.cycleYear}`,
          label: ctaLabel,
          url: potyUrl
        } : null,
        ctaAriaLabel: potyUrl ? i18nStore.getState().i18n.shared.learnMoreAbout({
          FEATURE: ctaFeature
        }) : undefined
      };
    });
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Achievements, {
  publicProps: {
    listing: {
      config: 0
    }
  },
  wire: {
    i18n: {
      adapter: WireI18n,
      config: function ($cmp) {
        return {};
      }
    },
    scrollToParam: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: selectScrollToQueryParam
        };
      }
    }
  }
});
const __lwc_component_class_internal = _registerComponent(Achievements, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;